import React, { useState, useCallback, memo, useContext, useRef, useEffect } from 'react';
import { PageTitle } from '../../../../_metronic/layout/core';
import { SessionsList } from './components/SessionsList';
import MessageView from './components/MessageView';
import { useCommunicationCenter } from './hooks/useCommunicationCenter';
import { Session, ConciergeMode } from './types/communication.types';
import { AuthContext } from '../../../context/authContext';
import { ConciergeModeService } from './services/conciergeModeService';
import Swal from 'sweetalert2';
// import WebSocketDiagnostic from './components/WebSocketDiagnostic';

/**
 * Central de Comunicação - Página principal
 * 
 * Esta é a página principal que integra todos os componentes da central de comunicação:
 * - Lista de sessões (lado esquerdo)
 * - Interface de chat (lado direito)
 * - Gerenciamento de estado global
 * - Layout responsivo
 */
const CommunicationCenter: React.FC = memo(() => {
  // Estado local para sessão ativa
  const [isMobileView, setIsMobileView] = useState(false);
  const markAsReadTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRenderRef = useRef(true); // Flag para detectar primeiro render

  // Contexto de autenticação - dados do usuário logado
  const { loginInfo } = useContext(AuthContext);

  // Hook principal de gerenciamento da central
  const hotelId = loginInfo.pluginspaceId || 1;
  const {
    sessions,
    activeSession,
    setActiveSession,
    isLoading,
    error,
    // stats, // Não utilizado no momento
    filters,
    setFilters,
    refreshSessions,
    markSessionAsRead,
    updateSessionConciergeMode,
    debugInfo,
    // 🤖 IA - Sugestões e análise (MÚLTIPLAS)
    aiSuggestions,
    isLoadingAISuggestion,
    acceptAISuggestion,
    discardAISuggestion
  } = useCommunicationCenter({ hotelId });

  /**
   * 🎯 EFEITO: Limpar sessão ativa quando o filtro de departamento mudar
   * 
   * Quando o usuário seleciona um filtro diferente, a área de mensagens
   * deve ser limpa para forçar a seleção de uma nova sessão da lista filtrada.
   */
  const departmentsKey = filters.departments.join(',');
  useEffect(() => {
    // Não fazer nada no primeiro render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    
    // Limpar sessão ativa sempre que o filtro mudar
    console.log('Filtro mudou, limpando sessão ativa');
    setActiveSession(null);
    setIsMobileView(false);
  }, [departmentsKey, setActiveSession]);

  /**
   * Callback para seleção de sessão
   */
  const handleSessionSelect = useCallback((session: Session) => {
    setActiveSession(session);
    setIsMobileView(true); // Em mobile, mostrar o chat
    
    // Cancelar timer anterior se existir
    if (markAsReadTimerRef.current) {
      clearTimeout(markAsReadTimerRef.current);
      markAsReadTimerRef.current = null;
    }
    
    // MARCAR COMO LIDA: Quando o usuário clica na sessão, marcar mensagens como lidas
    if (session.unreadCount && session.unreadCount > 0) {
      console.log(`Marcando sessão ${session.guestName} como lida imediatamente...`);
      
      // Marcar como lida imediatamente ao selecionar
      markSessionAsRead(session.sessionKey);
      
      // Atualizar localmente para remover o badge imediatamente
      setActiveSession({ ...session, unreadCount: 0 });
    }
  }, [setActiveSession, markSessionAsRead]);

  /**
   * Callback para voltar à lista (mobile)
   */
  const handleBackToSessions = useCallback(() => {
    setIsMobileView(false);
    setActiveSession(null);
  }, [setActiveSession]);

  /**
   * Callback para fechamento de sessão
   */
  const handleSessionClose = useCallback(() => {
    setActiveSession(null);
    setIsMobileView(false);
    refreshSessions();
  }, [refreshSessions, setActiveSession]);

  /**
   * Callback para abrir a página de edição da reserva em uma nova aba
   */
  const handleGoToReservation = useCallback(() => {
    if (activeSession?.reservationId) {
      const url = `/hospitality/hotel-reservation/editar/${activeSession.reservationId}`;
      window.open(url, '_blank');
    }
  }, [activeSession]);

  /**
   * Callback para mudança de modo de operação
   */
  const handleConciergeModeChange = useCallback(async (sessionKey: string, mode: ConciergeMode) => {
    const session = sessions.find(s => s.sessionKey === sessionKey);
    if (!session) return;

    try {
      const response = await ConciergeModeService.updateConciergeMode({
        sessionKey: session.sessionKey,
        hotelId: session.hotelId,
        room: session.room,
        reservationId: session.reservationId,
        conciergeMode: mode
      });

      if (response.success) {
        // Atualizar localmente PRIMEIRO (para feedback imediato)
        updateSessionConciergeMode(sessionKey, mode);
        
        // Atualizar a sessão ativa se for a mesma
        if (activeSession?.sessionKey === sessionKey) {
          setActiveSession({ ...activeSession, conciergeMode: mode });
        }

        Swal.fire({
          icon: 'success',
          title: 'Modo atualizado',
          text: response.message,
          timer: 2000,
          showConfirmButton: false
        });

        // ❌ REMOVIDO: refreshSessions() estava sobrescrevendo a atualização local
        // O updateSessionConciergeMode já atualizou o estado local
        // e o backend já confirmou a mudança
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: response.message
        });
      }
    } catch (error) {
      console.error('Erro ao atualizar concierge mode:', error);
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Não foi possível atualizar o modo de operação'
      });
    }
  }, [sessions, activeSession, setActiveSession, updateSessionConciergeMode]);

  return (
    <>
      {/* Título da página */}
      <PageTitle breadcrumbs={[]}>Central de Comunicação</PageTitle>

      {/* Container principal */}
      <div className="d-flex flex-column flex-lg-row gap-7 gap-lg-10" style={{ minHeight: 'calc(100vh - 200px)' }}>
        
        {/* Painel esquerdo - Lista de sessões */}
        <div className={`flex-column flex-lg-row-auto w-100 w-lg-300px w-xl-400px mb-7 mb-lg-0 ${isMobileView ? 'd-none d-lg-flex' : 'd-flex'}`}>
          
          {/* Card da lista de sessões */}
          <div className="card card-flush h-lg-100 d-flex flex-column">
            
            {/* Header com estatísticas */}
            <div className="card-header pt-7">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold text-gray-800">Central de Comunicação</span>
                {/* <span className="text-gray-400 mt-1 fw-semibold fs-6">
                  {stats ? `${stats.totalSessions} total • ${stats.unreadMessages} não lidas` : 'Carregando...'}
                </span> */}
              </h3>
              
              {/* Botão de atualizar */}
              {/* <div className="card-toolbar">
                <button 
                  type="button" 
                  className="btn btn-sm btn-light btn-icon" 
                  onClick={refreshSessions}
                  disabled={isLoading}
                  title="Atualizar sessões"
                >
                  <i className={`ki-duotone ki-arrows-circle fs-2 ${isLoading ? 'spinner-border spinner-border-sm' : ''}`}>
                    <span className="path1"></span>
                    <span className="path2"></span>
                  </i>
                </button>
              </div> */}
            </div>

            {/* Corpo do card */}
            <div className="card-body pt-2 flex-grow-1 overflow-auto">
              {/* Lista de sessões */}
              <SessionsList
                sessions={sessions}
                activeSession={activeSession}
                isLoading={isLoading}
                error={error}
                filters={filters}
                onFiltersChange={setFilters}
                onSessionSelect={handleSessionSelect}
                onRefresh={refreshSessions}
                onConciergeModeChange={handleConciergeModeChange}
                aiSuggestions={aiSuggestions}
                debugInfo={debugInfo}
              />
            </div>
          </div>
        </div>

        {/* Painel direito - Interface de chat */}
        <div className={`flex-lg-row-fluid ms-lg-7 ms-xl-10 ${!isMobileView ? 'd-none d-lg-block' : 'd-block'}`}>
          
          {activeSession ? (
            /* Chat ativo */
            <div className="card card-flush h-lg-100 d-flex flex-column">
              
              {/* Header do chat */}
              <div className="card-header">
                <div className="d-flex align-items-center justify-content-between w-100">
                  
                  {/* Lado esquerdo: Botão voltar + Info da sessão */}
                  <div className="d-flex align-items-center">
                    {/* Botão voltar (mobile) */}
                    <button 
                      type="button" 
                      className="btn btn-icon btn-sm btn-light-primary me-3 d-lg-none"
                      onClick={handleBackToSessions}
                    >
                      <i className="ki-duotone ki-arrow-left fs-2">
                        <span className="path1"></span>
                        <span className="path2"></span>
                      </i>
                    </button>

                    {/* Avatar + Informações do hóspede */}
                    <div className="d-flex align-items-center">
                      <div className={`symbol symbol-35px me-3 ${activeSession.connected ? 'symbol-circle' : ''}`}>
                        <div className={`symbol-label fs-3 fw-bold text-white ${
                          activeSession.connected ? 'bg-success' : 'bg-secondary'
                        }`}>
                          {activeSession.guestName?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        {activeSession.connected && (
                          <div className='symbol-badge bg-success start-100 top-100 border-4 h-8px w-8px ms-n2 mt-n2'></div>
                        )}
                      </div>
                      <div>
                        <div className='fw-bold text-gray-900 fs-6'>{activeSession.guestName}</div>
                        <div className='text-muted fs-7'>
                          Quarto {activeSession.room} • {activeSession.department}
                          {activeSession.connected ? (
                            <span className='badge badge-light-success ms-2'>Online</span>
                          ) : (
                            <span className='badge badge-light-secondary ms-2'>Offline</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lado direito: Botão fechar */}
                  <div className="d-flex align-items-center gap-2">
                    
                    {/* Status da sessão - Removido por não estar na interface */}
                    <div className="d-flex align-items-center gap-2 d-none">
                      <select 
                        className="form-select form-select-sm w-auto"
                        defaultValue="open"
                      >
                        <option value="open">Aberta</option>
                        <option value="assigned">Atribuída</option>
                        <option value="pending">Pendente</option>
                        <option value="resolved">Resolvida</option>
                        <option value="closed">Fechada</option>
                      </select>
                    </div>

                    {/* Botão para acessar a reserva */}
                    <button 
                      type="button" 
                      className="btn btn-sm btn-light-primary"
                      onClick={handleGoToReservation}
                      title="Ver reserva"
                    >
                      <i className="ki-duotone ki-calendar fs-2 me-2">
                        <span className="path1"></span>
                        <span className="path2"></span>
                      </i>
                      Ver Reserva
                    </button>

                    {/* Botão fechar */}
                    <button 
                      type="button" 
                      className="btn btn-sm btn-light-danger"
                      onClick={handleSessionClose}
                      title="Fechar sessão"
                    >
                      <i className="ki-duotone ki-cross fs-2 me-2">
                        <span className="path1"></span>
                        <span className="path2"></span>
                      </i>
                      Fechar
                    </button>
                  </div>
                </div>
              </div>

              {/* Interface de chat */}
              <div className="card-body p-0 d-flex flex-column h-100">
                <MessageView 
                  session={activeSession}
                  onClose={handleSessionClose}
                  agentId={loginInfo.id.toString()}
                  agentEmail={loginInfo.email}
                  agentName={loginInfo.name}
                  hotelId={hotelId}
                  selectedDepartment={filters.departments.length > 0 ? filters.departments[0] : null}
                  aiSuggestions={aiSuggestions}
                  isLoadingAISuggestion={isLoadingAISuggestion}
                  onAcceptAISuggestion={acceptAISuggestion}
                  onDiscardAISuggestion={discardAISuggestion}
                />
              </div>
            </div>
          ) : (
            /* Estado vazio - Nenhuma sessão selecionada */
            <div className="card card-flush h-lg-100 d-flex flex-column">
              <div className="card-body d-flex flex-column justify-content-center align-items-center text-center py-20">
                
                {/* Ícone */}
                <div className="mb-7">
                  <i className="ki-duotone ki-message-text-2 fs-4x text-gray-400">
                    <span className="path1"></span>
                    <span className="path2"></span>
                    <span className="path3"></span>
                  </i>
                </div>

                {/* Texto */}
                <div className="mb-7">
                  <h3 className="text-gray-800 fw-bold mb-2">Selecione uma sessão</h3>
                  <p className="text-gray-400 fw-semibold fs-6 mb-0">
                    Escolha uma sessão da lista à esquerda para iniciar a conversa
                  </p>
                </div>

                {/* Dicas */}
                <div className="text-start">
                  <div className="d-flex align-items-center mb-3">
                    <i className="ki-duotone ki-check-circle fs-2 text-success me-3">
                      <span className="path1"></span>
                      <span className="path2"></span>
                    </i>
                    <span className="text-gray-600 fw-semibold">
                      Gerencie conversas em tempo real
                    </span>
                  </div>
                  
                  <div className="d-flex align-items-center mb-3">
                    <i className="ki-duotone ki-flash-circle fs-2 text-warning me-3">
                      <span className="path1"></span>
                      <span className="path2"></span>
                    </i>
                    <span className="text-gray-600 fw-semibold">
                      Use sugestões de IA para respostas
                    </span>
                  </div>
                  
                  <div className="d-flex align-items-center">
                    <i className="ki-duotone ki-directbox-default fs-2 text-primary me-3">
                      <span className="path1"></span>
                      <span className="path2"></span>
                      <span className="path3"></span>
                      <span className="path4"></span>
                    </i>
                    <span className="text-gray-600 fw-semibold">
                      Filtre por departamento e status
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Componente de Diagnóstico WebSocket - Oculto */}
      {/* <div className="mt-10">
        <WebSocketDiagnostic />
      </div> */}
    </>
  );
});

CommunicationCenter.displayName = 'CommunicationCenter';

export { CommunicationCenter };
export default CommunicationCenter;